import type { AlgorithmChallenge } from '@code-arcade/engine'

export const binarySearch: AlgorithmChallenge = {
  id: 'binary-search',
  title: 'Binary Search',
  level: 4,
  chapter: 'chapter-6',
  mode: 'race-it',
  description: 'Halve the search space each step on a sorted dataset — O(log n) to find any boundary.',

  realWorld: {
    scenario: "ByteForge's acquirer runs 40 due-diligence queries across 3 years of sorted deployment metrics: when did p99 latency first exceed 500ms? Which deploy introduced the memory leak? When did error rate first cross 1%?",
    consequence: 'Linear scan: O(n) per query × 40 queries × 3M data points = hours of review time. Binary search: O(log n) = 22 comparisons to find any boundary in 3M sorted records. All 40 queries resolve in under a second.',
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 20,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 8,
      character: 'vera-lox',
      mood: 'stressed',
      message: "The acquirer wants all 40 metric queries answered in the next hour. 3 years of data.",
    },
  ],

  complexity: { time: 'O(log n)', space: 'O(1)' },
  xpReward: { oneStar: 250, twoStar: 500, threeStar: 1000 },

  keyTakeaway:
    'Binary search is not a sorted-array trick — it\'s a strategy for any monotonic predicate: ' +
    '"find the first point where condition becomes true." `git bisect` is binary search on commit history. ' +
    'Finding the minimum server count that passes your load test is binary search on a parameter space. ' +
    'Database index range scans are binary search on B-tree nodes. Any time you have an ordered space ' +
    'and need to find a boundary, you are doing binary search whether you name it or not. ' +
    'The pattern eliminates O(n) work wherever a sorted invariant exists — and sorted invariants are everywhere.',

  whenToUse: [
    '`git bisect` — find the first commit where a test fails; O(log n) builds instead of O(n)',
    'Find the first config threshold that causes failure — minimum memory, maximum concurrency',
    'Database B-tree index lookups — every indexed WHERE clause is binary search',
    'Sorted log search: find the first ERROR entry after a deploy timestamp',
    'Feature flag rollout: find the first user ID bucket where the flag should activate',
    'Any "find first X where condition is true" on a monotonic predicate',
  ],

  whenNotToUse: [
    'The data is unsorted — sort it first (O(n log n) once, then O(log n) per query)',
    'You\'re only searching once and n is tiny — linear scan is simpler and fast enough',
    'The predicate is not monotonic — binary search requires that once condition is true, it stays true',
  ],

  pseudocode: `procedure binarySearch(A, target)
  left ← 0, right ← length(A) − 1
  while left ≤ right
    mid ← ⌊(left + right) / 2⌋
    if A[mid] = target  then return mid
    if A[mid] < target  then left ← mid + 1
    else                     right ← mid − 1
  return −1  // not found

// General form: find first index where predicate(A[i]) is true
procedure lowerBound(A, predicate)
  left ← 0, right ← length(A)
  while left < right
    mid ← ⌊(left + right) / 2⌋
    if predicate(A[mid]) then right ← mid
    else                       left ← mid + 1
  return left`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static int BinarySearch(int[] arr, int target) {
    int left = 0, right = arr.Length - 1;
    while (left <= right) {
        int mid = left + (right - left) / 2;   // avoids overflow
        if (arr[mid] == target) return mid;
        if (arr[mid] < target)  left  = mid + 1;
        else                    right = mid - 1;
    }
    return -1;
}

// Built-in equivalent — binary search on sorted arrays:
int idx = Array.BinarySearch(arr, target);
// Returns index if found, or bitwise complement of insertion point if not`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def binary_search(arr: list[int], target: int) -> int:
    left, right = 0, len(arr) - 1
    while left <= right:
        mid = (left + right) // 2
        if arr[mid] == target:   return mid
        elif arr[mid] < target:  left = mid + 1
        else:                    right = mid - 1
    return -1

# Built-in — binary search in Python:
import bisect
idx = bisect.bisect_left(arr, target)   # O(log n), finds insertion point
# Also: bisect_right, insort — for finding ranges and inserting in sorted order`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function binarySearch(arr, target) {
  let left = 0, right = arr.length - 1;
  while (left <= right) {
    const mid = (left + right) >>> 1;  // unsigned right shift — safe midpoint
    if (arr[mid] === target) return mid;
    if (arr[mid] < target)   left  = mid + 1;
    else                     right = mid - 1;
  }
  return -1;
}

// No built-in binary search in JS — but you now know exactly why it works`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function binarySearch(arr: number[], target: number): number {
  let left = 0, right = arr.length - 1;
  while (left <= right) {
    const mid = (left + right) >>> 1;
    if (arr[mid] === target) return mid;
    if (arr[mid] < target)   left  = mid + 1;
    else                     right = mid - 1;
  }
  return -1;
}

// No built-in binary search in TS/JS — but you now know exactly why it works`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static int binarySearch(int[] arr, int target) {
    int left = 0, right = arr.length - 1;
    while (left <= right) {
        int mid = left + (right - left) / 2;   // avoids int overflow
        if (arr[mid] == target) return mid;
        if (arr[mid] < target)  left  = mid + 1;
        else                    right = mid - 1;
    }
    return -1;
}

// Built-in equivalent:
int idx = Arrays.binarySearch(arr, target);  // O(log n) on sorted array`,
    },
  ],

  sampleDataset: {
    label: 'ByteForge deployment p99 latency (ms) — 3 years, sorted by timestamp',
    preview: [41, 43, 45, 48, 52, 55, 61, 68, 120, 380, 510, 890],
    atScale: {
      n: 3000000,
      leftLabel: 'Linear scan',
      leftNote: 'up to 3M comparisons',
      rightLabel: 'Binary Search',
      rightNote: '≤ 22 comparisons',
    },
  },
}
